import React, { useEffect, Fragment, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import Swal from "sweetalert2";
import { UncontrolledTooltip } from "reactstrap";

import Breadcrumb from "../../../../components/common/breadcrumb";
import { showToast } from "../../../../components/ToastifyNotification";

import { DeletePatientHistory, GetPatientHistories } from "../../../../api/ipd/patientHistoryAPI";
import { DeleteDeathCertificate, GetDeathCertificates } from "../../../../api/ipd/deathCertificateAPI";

const ListDeathCertificateEntry = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // ================= FETCH DATA =================
  const fetchData = async () => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await GetDeathCertificates();
      if (response.success) {
        showToast("success", response.message);
        const formattedData = response.data.map((item, index) => ({
          index: index + 1,
          id: item._id,

          certificateNo: item?.certificateNo || "",
          ipdNo: item?.ipdEntryId?.ipdNo || "",
          patientName: item?.ipdEntryId?.patientId?.patientName || "",
          sex: item?.ipdEntryId?.patientId?.sex || "",
          age: item?.ipdEntryId?.patientId?.age || "",

          dateOfIssue: item.dateOfIssue.slice(0, 10) || "",
          dateOfDeath: item.dateOfDeath.slice(0, 10) || "",
          timeOfDeath: item.timeOfDeath || "",
          causeOfDeath: item.causeOfDeath || "",
          consultantId: item.consultantId?.name || "",
        }));

        setData(formattedData);
        setFilteredData(formattedData);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ================= SEARCH =================
  useEffect(() => {
    const results = data.filter(item =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
    setFilteredData(results);
  }, [search, data]);

  // ================= ACTIONS =================
  const handleEditClick = id => {
    navigate(`/ipd/transaction/death-certificate-entry/edit/${id}`);
  };

  const handleDeleteClick = id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This delivery entry will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then(async result => {
      if (result.isConfirmed) {
        dispatch({ type: "LOADER_START" });
        try {
          const response = await DeleteDeathCertificate(id);
          if (response.success) {
            showToast("success", response.message);
            const newData = data.filter(item => item.id !== id);
            setData(newData);
            setFilteredData(newData);
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", error.message);
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      }
    });
  };

  // ================= TABLE COLUMNS =================
  const columns = [
    { name: "S.No", selector: row => row.index, width: "6%" },

    { name: "Certificate No", selector: row => row.certificateNo, width: "15%" },
    { name: "IPD No", selector: row => row.ipdNo, width: "15%" },
    { name: "Patient Name", selector: row => row.patientName, width: "15%" },
    { name: "Sex", selector: row => row.sex, width: "15%" },
    { name: "Age", selector: row => row.age, width: "15%" },

    { name: "Date of Issue", selector: row => row.dateOfIssue, width: "15%" },
    { name: "Date of Death", selector: row => row.dateOfDeath, width: "15%" },
    { name: "Time of Death", selector: row => row.timeOfDeath, width: "15%" },
    { name: "Cause of Death", selector: row => row.causeOfDeath, width: "15%" },
    { name: "Consultant", selector: row => row.consultantId, width: "15%" },

    {
      name: "Actions",
      cell: row => (
        <div>
          <button
            className="btn btn-primary me-2"
            onClick={() => handleEditClick(row.id)}
            id={`edit-${row.id}`}
          >
            <i className="fa fa-pencil" />
          </button>
          <UncontrolledTooltip target={`edit-${row.id}`}>
            Edit
          </UncontrolledTooltip>

          <button
            className="btn btn-danger"
            onClick={() => handleDeleteClick(row.id)}
            id={`delete-${row.id}`}
          >
            <i className="fa fa-trash" />
          </button>
          <UncontrolledTooltip target={`delete-${row.id}`}>
            Delete
          </UncontrolledTooltip>
        </div>
      ),
      ignoreRowClick: true,
      button: true,
      width: "20%"
    }
  ];

  // ================= CSV EXPORT =================
  const headers = [
    { label: "S.No", key: "index" },

    { label: "Certificate No", key: "certificateNo" },
    { label: "IPD No", key: "ipdNo" },
    { label: "Patient Name", key: "patientName" },
    { label: "Sex", key: "sex" },
    { label: "Age", key: "age" },

    { label: "Date of Issue", key: "dateOfIssue" },
    { label: "Date of Death", key: "dateOfDeath" },
    { label: "Time of Death", key: "timeOfDeath" },
    { label: "Cause of Death", key: "causeOfDeath" },
    { label: "Consultant", key: "consultantId" },
  ];

  const ExportCSV = ({ data }) =>
      <CSVLink data={data} headers={headers} filename="death-certificate-entries.csv">
        <button className="btn btn-primary mt-1" id="export">
          Export
        </button>
        <UncontrolledTooltip placement="top" target="export">
          Export to CSV
        </UncontrolledTooltip>
      </CSVLink>;

  // ================= RENDER =================
  return (
    <Fragment>
      <Breadcrumb parent="Death Certificate" title="Death Certificate Entries" />

      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex justify-content-between">
            <h5>Death Certificate Entries</h5>

            <Link to="/ipd/transaction/death-certificate-entry/add">
              <button className="btn btn-primary" id="create">
                Create Death Certificate Entry
              </button>
              <UncontrolledTooltip placement="top" target="create">
                Create New Death Certificate Entry
              </UncontrolledTooltip>
            </Link>
          </div>

          <div className="card-body">
            <div className="row mb-2">
              <div className="col-md-6">
                <input
                  className="form-control"
                  placeholder="Search..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>

              <div className="col-md-6 text-end">
                <ExportCSV data={data} />
              </div>
            </div>

            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ListDeathCertificateEntry;
